import mixins from '../formula-handler-mixins';

const { methods: { 
    count, 
    copy, 
    dateAdjust, 
    difference, 
    divide,
    dynamicContent,
    join, 
    multiply, 
    sum, 
    or 
} } = mixins;

// Tests the count method of the DynamicInput component
describe('Formula Handlers', () => {
    describe('copy', () => {
        it('should return the value of the target', () => {
            expect(copy({
                targets: ['test']
            }, {
                test: 'test'
            })).toEqual('test');
        });
    });

    describe('count', () => {
        it('should return 0 when no items are passed', () => {
            expect(count({
                targets: []
            }, {})).toEqual(0);
        });

        it('should error when a non-array is passed', () => {
            expect(() => {
                count({
                    targets: ['test']
                }, {
                    test: 'not an array'
                });
            }).toThrow();
        });

        it('should return the length of the array when an array is passed', () => {
            expect(count({
                targets: ['test']
            }, {
                test: [1, 2, 3]
            })).toEqual(3);
        });

        it('should not count items that do not meet dependencies', () => {
            expect(count({
                targets: ['test'],
                dependencies: {
                    items: [
                        {
                            name: 'test',
                            value: 'true'
                        }
                    ]
                }
            }, {
                test: [
                    {
                        test: 'false'
                    },
                    {
                        test: 'true'
                    }
                ]
            })).toEqual(1);
        });
    });

    describe('dateAdjust', () => {
        it('should return the expected date when using preset "trailingTwelveMonths"', () => {
            const actual = dateAdjust({
                targets: ['endDate'],
                preset: 'trailingTwelveMonths'
            }, {
                endDate: '2023-05-31'
            });
            const expected = '2022-06-01';

            expect(actual).toEqual(expected);
        });

        it('should return the expected date when using preset "trailingTwelveMonths" and end date is after a leap day', () => {
            const actual = dateAdjust({
                targets: ['endDate'],
                preset: 'trailingTwelveMonths'
            }, {
                endDate: '2024-05-31'
            });
            const expected = '2023-06-01';

            expect(actual).toEqual(expected);
        });

        it('should return the expected date when using preset "trailingTwelveMonths" and trailing date is before a leap day', () => {
            const actual = dateAdjust({
                targets: ['endDate'],
                preset: 'trailingTwelveMonths'
            }, {
                endDate: '2025-01-31'
            });
            const expected = '2024-02-01';

            expect(actual).toEqual(expected);
        });

        it('should return the expected date when using preset "trailingTwelveMonths" and trailing date in the same calendar year', () => {
            const actual = dateAdjust({
                targets: ['endDate'],
                preset: 'trailingTwelveMonths'
            }, {
                endDate: '2024-12-31'
            });
            const expected = '2024-01-01';

            expect(actual).toEqual(expected);
        });

        it('should return the expected date when using preset "trailingTwelveMonths" and end date is a leap day', () => {
            const actual = dateAdjust({
                targets: ['endDate'],
                preset: 'trailingTwelveMonths'
            }, {
                endDate: '2024-02-29'
            });
            const expected = '2023-03-01';

            expect(actual).toEqual(expected);
        });

        it('should return the expected date when using preset "trailingTwelveMonths" and trailing date is a leap day', () => {
            const actual = dateAdjust({
                targets: ['endDate'],
                preset: 'trailingTwelveMonths'
            }, {
                endDate: '2025-02-28'
            });
            const expected = '2024-02-29';

            expect(actual).toEqual(expected);
        });
    });

    describe('difference', () => {
        it('should return 0 when no items are passed', () => {
            expect(difference({
                targets: []
            }, {})).toEqual(0);
        });

        it('should return the difference between two numbers', () => {
            expect(difference({
                targets: ['test', 'test2']
            }, {
                test: 5,
                test2: 3
            })).toEqual(2); // 5 - 3 = 2
        });

        it('should return the difference between three numbers', () => {
            expect(difference({
                targets: ['test', 'test2', 'test3']
            }, {
                test: 5,
                test2: 3,
                test3: 2
            })).toEqual(0); // 5 - 3 - 2 = 0
        });

        it('should return the difference when one of the targets is an object with a value property', () => {
            expect(difference({
                targets: ['test', { value: 3 }, 'test3']
            }, {
                test: 5,
                test3: 2
            })).toEqual(0); // 5 - 3 - 2 = 0
        });

        it('should return the expected difference of the targets when one of the target values is an array of objects', () => {
            expect(difference({
                targets: ['test', 'test2'],
                targetField: 'test'
            }, {
                test: 5,
                test2: [
                    {
                        test: 3
                    },
                    {
                        test: 2
                    }
                ]
            })).toEqual(4); // 5 - (3 - 2) = 4
        });

        it('should return the expected difference of the targets when one of the target values is an array of objects and includes dependencies', () => {
            expect(difference({
                targets: ['test', 'test2'],
                targetField: 'test',
                dependencies: {
                    items: [
                        {
                            name: 'testDep',
                            value: 'true'
                        }
                    ]
                }
            }, {
                test: 5,
                test2: [
                    {
                        test: 3,
                        testDep: 'false'
                    },
                    {
                        test: 2,
                        testDep: 'true'
                    }
                ]
            })).toEqual(3); // 5 - 2 = 3
        });

        it('should convert all strings to computable numbers', () => {
            expect(difference({
                targets: ['test', { value: '3'}, 'test2'],
                targetField: 'test'
            }, {
                test: '5',
                test2: [
                    {
                        test: '3'
                    },
                    {
                        test: 2
                    }
                ]
            })).toEqual(1); // 5 - 3 - (3 - 2) = 1
        });

        it('should override targets with values property if provided', () => {
            // Formula will listen to test, test2 and test3.
            // It will only calculate the difference between test and test2.
            expect(difference({
                targets: ['test', 'test2', 'test3'],
                values: ['test', 'test2'],
            }, {
                test: 5,
                test2: 3,
                test3: 2
            })).toEqual(2); // 5 - 3 = 2
        });
    });

    describe('divide', () => {
        it('should return 0 when no items are passed', () => {
            expect(difference({
                targets: []
            }, {})).toEqual(0);
        });

        it('should return the quotient of two numbers', () => {
            expect(divide({
                targets: ['test', 'test2']
            }, {
                test: 6,
                test2: 3
            })).toEqual(2); // 6 / 3 = 2
        });

        it('should return the quotient of three numbers', () => {
            expect(divide({
                targets: ['test', 'test2', 'test3']
            }, {
                test: 6,
                test2: 3,
                test3: 2
            })).toEqual(1); // 6 / 3 / 2 = 1
        });

        it('should return the quotient when one of the targets is an object with a value property', () => {
            expect(divide({
                targets: ['test', { value: 3 }, 'test3']
            }, {
                test: 6,
                test3: 2
            })).toEqual(1); // 6 / 3 / 2 = 1
        });

        it('should return the expected quotient of the targets when one of the target values is an array of objects', () => {
            expect(divide({
                targets: ['test', 'test2'],
                targetField: 'test'
            }, {
                test: 6,
                test2: [
                    {
                        test: 4
                    },
                    {
                        test: 2
                    }
                ]
            })).toEqual(3); // 6 / (4 / 2) = 3
        });

        it('should return the expected quotient of the targets when one of the target values is an array of objects and includes dependencies', () => {
            expect(divide({
                targets: ['test', 'test2'],
                targetField: 'test',
                dependencies: {
                    items: [
                        {
                            name: 'testDep',
                            value: 'true'
                        }
                    ]
                }
            }, {
                test: 6,
                test2: [
                    {
                        test: 4,
                        testDep: 'false'
                    },
                    {
                        test: 2,
                        testDep: 'true'
                    }
                ]
            })).toEqual(3); // 6 / 2 = 3
        });

        it('should convert all strings to computable numbers', () => {
            expect(divide({
                targets: ['test', { value: '3'}, 'test2'],
                targetField: 'test'
            }, {
                test: '6',
                test2: [
                    {
                        test: '4'
                    },
                    {
                        test: 2
                    }
                ]
            })).toEqual(1); // 6 / 3 / (4 / 2) = 1
        });

        it('should override targets with values property if provided', () => {
            // Formula will listen to test, test2 and test3.
            // It will only calculate the sum of test and test2.
            expect(divide({
                targets: ['test', 'test2', 'test3'],
                values: ['test', 'test2'],
            }, {
                test: 6,
                test2: 3,
                test3: 2
            })).toEqual(2); // 6 /3 = 2
        });
    });

    describe('dynamicContent', () => {
        it('should return the default item if no criteria are present', () => {
            const expected = 'default result';
            const actual = dynamicContent({
                values: [
                    {
                        value: 'default result'
                    }
                ]
            }, {
                test: 'true'
            });

            expect(actual).toEqual(expected);
        });

        it('should return the first result if it matches criteria', () => {
            const expected = 'result 1';
            const actual = dynamicContent({
                values: [
                    {
                        value: 'result 1',
                        dependencies: {
                            items: [
                                {
                                    name: 'test',
                                    value: 'true'
                                }
                            ]
                        }
                    },
                    {
                        value: 'default result'
                    },
                ]
            }, {
                test: 'true'
            });

            expect(actual).toEqual(expected);
        });

        it('should return the second result if it matches criteria and first does not match', () => {
            const expected = 'result 2';
            const actual = dynamicContent({
                values: [
                    {
                        value: 'result 1',
                        dependencies: {
                            items: [
                                {
                                    name: 'test',
                                    value: 'false'
                                }
                            ]
                        }
                    },
                    {
                        value: 'result 2',
                        dependencies: {
                            items: [
                                {
                                    name: 'test',
                                    value: 'true'
                                }
                            ]
                        }
                    },
                    {
                        value: 'default result'
                    },
                ]
            }, {
                test: 'true'
            });

            expect(actual).toEqual(expected);
        });

        it('should return the default result if no other criteria match', () => {
            const expected = 'default result';
            const actual = dynamicContent({
                values: [
                    {
                        value: 'result 1',
                        dependencies: {
                            items: [
                                {
                                    name: 'test',
                                    value: 'false'
                                }
                            ]
                        }
                    },
                    {
                        value: 'result 2',
                        dependencies: {
                            items: [
                                {
                                    name: 'test',
                                    value: 'also false'
                                }
                            ]
                        }
                    },
                    {
                        value: 'default result'
                    },
                ]
            }, {
                test: 'true'
            });

            expect(actual).toEqual(expected);
        });

        it('should return an empty string if no criteria are met', () => {
            const expected = '';
            const actual = dynamicContent({
                values: [
                    {
                        value: 'result 1',
                        dependencies: {
                            items: [
                                {
                                    name: 'test',
                                    value: 'false'
                                }
                            ]
                        }
                    },
                    {
                        value: 'result 2',
                        dependencies: {
                            items: [
                                {
                                    name: 'test',
                                    value: 'also false'
                                }
                            ]
                        }
                    }
                ]
            }, {
                test: 'true'
            });

            expect(actual).toEqual(expected);
        });

        it('should return display text when useDisplay is provided in formula arguments', () => {
            const expected = 'Qualified Buyer';
            const actual = dynamicContent.call({
                structureData: {
                    items: [
                        {
                            name: 'buyer-status',
                            props: {
                                items: [
                                    {
                                        value: 'qualified',
                                        text: 'Qualified Buyer'
                                    }
                                ]
                            }
                        }
                    ]
                }
            }, {
                useDisplay: 'buyer-status',
                values: [
                    {
                        value: 'qualified'
                    }
                ]
            }, {
                test: 'true'
            });

            expect(actual).toEqual(expected);
        });

        it('should return display text when useDisplay references a props.items source key', () => {
            const expected = 'President';
            const actual = dynamicContent.call({
                structureData: {
                    items: [
                        {
                            name: 'ds-signing-titles',
                            props: {
                                items: [
                                    {
                                        value: 'president',
                                        text: 'President'
                                    }
                                ]
                            }
                        },
                        {
                            name: 'signer-title',
                            props: {
                                items: 'ds-signing-titles'
                            }
                        }
                    ]
                }
            }, {
                values: [
                    {
                        value: {
                            formula: 'copy',
                            arguments: {
                                useDisplay: 'ds-signing-titles',
                                targets: ['signer-title']
                            }
                        },
                        dependencies: {
                            items: [
                                {
                                    name: 'signer-title',
                                    operator: 'isDefined'
                                }
                            ]
                        }
                    }
                ]
            }, {
                'signer-title': 'president'
            });

            expect(actual).toEqual(expected);
        });

        it('should return display text when structure is only available on a parent context', () => {
            const expected = 'President';
            const actual = dynamicContent.call({
                $parent: {
                    copyStructureData: {
                        items: [
                            {
                                name: 'ds-signing-titles',
                                props: {
                                    items: [
                                        {
                                            value: 'president',
                                            text: 'President'
                                        }
                                    ]
                                }
                            },
                            {
                                name: 'signer-title',
                                props: {
                                    items: 'ds-signing-titles'
                                }
                            }
                        ]
                    }
                }
            }, {
                values: [
                    {
                        value: {
                            formula: 'copy',
                            arguments: {
                                useDisplay: 'ds-signing-titles',
                                targets: ['signer-title']
                            }
                        },
                        dependencies: {
                            items: [
                                {
                                    name: 'signer-title',
                                    operator: 'isDefined'
                                }
                            ]
                        }
                    }
                ]
            }, {
                'signer-title': 'president'
            });

            expect(actual).toEqual(expected);
        });
    });

    describe('join', () => {
        it('should return an empty string when no items are passed', () => {
            expect(join({
                targets: []
            }, {})).toEqual('');
        });

        it('should return a string when items are passed', () => {
            expect(join({
                targets: ['test', 'test2']
            }, {
                test: 'test',
                test2: 'test2'
            })).toEqual('test test2');
        });

        it('should return a string when items are passed with a delimiter', () => {
            expect(join({
                targets: ['test', 'test2'],
                delimiter: '-'
            }, {
                test: 'test',
                test2: 'test2'
            })).toEqual('test-test2');
        });
    });

    describe('or', () => {
        it('should return empty string when to items are passed', () => {
            expect(or({
                targets: []
            }, {})).toEqual('');
        });
    
        it('should return the first item when two items are passed', () => {
            expect(or({
                targets: ['test', 'test2']
            }, {
                test: 'test',
                test2: 'test2'
            })).toEqual('test');
        });

        it('should return the second item when the first item is empty', () => {
            expect(or({
                targets: ['test', 'test2']
            }, {
                test: null,
                test2: 'test2'
            })).toEqual('test2');
        });
    });

    describe('sum', () => {
        it('should return 0 when no items are passed', () => {
            expect(sum({
                targets: []
            }, {})).toEqual('0');
        });

        it('should return the sum of the targets', () => {
            expect(sum({
                targets: ['test', 'test2']
            }, {
                test: 5,
                test2: 3
            })).toEqual('8'); // 5 + 3 = 8
        });

        it('should return the sum when one of the targets is an object with a value property', () => {
            expect(sum({
                targets: ['test', { value: 3 }, 'test3']
            }, {
                test: 5,
                test3: 2
            })).toEqual('10'); // 5 + 3 + 2 = 10
        });

        it('should return the sum of the targets when one of the target values is an array of objects', () => {
            expect(sum({
                targets: ['test', 'test2'],
                targetField: 'test'
            }, {
                test: 5,
                test2: [
                    {
                        test: 3
                    },
                    {
                        test: 2
                    }
                ]
            })).toEqual('10'); // 5 + (3 + 2) = 10
        });

        it('should return the sum of the targets when one of the target values is an array of objects and includes dependencies', () => {
            expect(sum({
                targets: ['test', 'test2'],
                targetField: 'test',
                dependencies: {
                    items: [
                        {
                            name: 'testDep',
                            value: 'true'
                        }
                    ]
                }
            }, {
                test: 5,
                test2: [
                    {
                        test: 3,
                        testDep: 'false'
                    },
                    {
                        test: 2,
                        testDep: 'true'
                    }
                ]
            })).toEqual('7'); // 5 + 2 = 7
        });

        it('should override targets with values property if provided', () => {
            // Formula will listen to test, test2 and test3.
            // It will only calculate the sum of test and test2.
            expect(sum({
                targets: ['test', 'test2', 'test3'],
                values: ['test', 'test2'],
            }, {
                test: 5,
                test2: 3,
                test3: 2
            })).toEqual('8'); // 5 + 3 = 8
        });
    });

    describe('multiply', () => {
        it('should return 0 when no items are passed', () => {
            expect(multiply({
                targets: []
            }, {})).toEqual(0);
        });

        it('should return the product of two numbers', () => {
            expect(multiply({
                targets: ['test', 'test2']
            }, {
                test: 2,
                test2: 3
            })).toEqual(6); // 2 * 3 = 6
        });

        it('should return the product of three numbers', () => {
            expect(multiply({
                targets: ['test', 'test2', 'test3']
            }, {
                test: 2,
                test2: 3,
                test3: 4
            })).toEqual(24); // 2 * 3 * 4 = 24
        });

        it('should return the product when one of the targets is an object with a value property', () => {
            expect(multiply({
                targets: ['test', { value: 3 }, 'test3']
            }, {
                test: 2,
                test3: 4
            })).toEqual(24); // 2 * 3 * 4 = 24
        });

        it('should return the expected product of the targets when one of the target values is an array of objects', () => {
            expect(multiply({
                targets: ['test', 'test2'],
                targetField: 'test'
            }, {
                test: 2,
                test2: [
                    {
                        test: 3
                    },
                    {
                        test: 4
                    }
                ]
            })).toEqual(24); // 2 * (3 * 4) = 24
        });

        it('should return the expected quotient of the targets when one of the target values is an array of objects and includes dependencies', () => {
            expect(multiply({
                targets: ['test', 'test2'],
                targetField: 'test',
                dependencies: {
                    items: [
                        {
                            name: 'testDep',
                            value: 'true'
                        }
                    ]
                }
            }, {
                test: 2,
                test2: [
                    {
                        test: 3,
                        testDep: 'false'
                    },
                    {
                        test: 4,
                        testDep: 'true'
                    }
                ]
            })).toEqual(8); // 2 * 4 = 8
        });

        it('should override targets with values property if provided', () => {
            // Formula will listen to test, test2 and test3.
            // It will only calculate the product of test and test2.
            expect(multiply({
                targets: ['test', 'test2', 'test3'],
                values: ['test', 'test2'],
            }, {
                test: 2,
                test2: 3,
                test3: 4
            })).toEqual(6); // 2 * 3 = 6
        });

        it('should convert all strings to computable numbers', () => {
            expect(multiply({
                targets: ['test', { value: '3'}, 'test2'],
                targetField: 'test'
            }, {
                test: '2',
                test2: [
                    {
                        test: '4'
                    },
                    {
                        test: 2
                    }
                ]
            })).toEqual(48); // 2 * 3 * (4 * 2) = 48
        });
    });
});


// WEBPACK FOOTER //
// ./src/components/questionnaire_template/mixins/__tests__/formula-handler-mixins.test.js